/* =============================================================================
   VIEWS / ADMIN / DASHBOARD-SHIFT — Kachel „Meine Schicht" (S237 T2)
   =============================================================================
   Ersetzt die Kachel „Umsatz heute" auf dem Dashboard. Jonas-Entscheid S237:
   Umsatz gehört NICHT aufs Dashboard (er steht in Auswertung + Tagesabschluss),
   stattdessen sieht der aktive Operator seine EIGENEN Tätigkeiten von heute:

       Aufgenommen · Zubereitet · Serviert · Kassiert

   Bewusst OHNE jeden Euro-Wert (auch nicht `paid_revenue_cents`) — Service/
   Staff-Geräte sollen keinen Umsatz anzeigen.

   Datenquelle: RPC `stats_operator_productivity(p_from, p_to)` (phase_140,
   audit_log-basiert, tz Europe/Berlin) — dieselbe Quelle wie die Vollansicht
   Auswertung → Personal (stats-personal.js). Zuordnung über
   `audit_log.operator_id` = `users.id` = `getOperator().userId`.

   Loop-Sicherheit (Memory gg-microtask-render-loop):
     - Inflight-Guard + 1,5 s Trailing-Debounce um jeden RPC-Aufruf.
     - Nach der Antwort wird NUR die Kachel gepatcht (`#dash-shift-card`,
       outerHTML — Muster `refreshOperatorCardDom`), NIE `renderDashboard()`.
     - Modul-Cache mit Key `userId|YYYY-MM-DD`; der Datumswechsel um Mitternacht
       verwirft ihn automatisch (das Cockpit läuft durch).

   Exports (Pure zuerst — Vitest):
     - pickOperatorRow(rpcJson, userId)
     - shiftCounters(row)
     - shiftSubLabel(op)
     - renderShiftWidget({ op, counters, loading })
     - refreshShiftCounters() / clearShiftCache() / wireShiftEvents()
============================================================================ */

import { htmlEscape, todayISO } from '../../utils/helpers.js';

/* -------------------------------------------------------------------------- */
/* Pure Helfer                                                                 */
/* -------------------------------------------------------------------------- */

/** Zeile des gesuchten Operators aus der RPC-Antwort. null, wenn er heute noch
 *  keine attribuierte Aktion hat (oder die Antwort leer/kaputt ist). Pure. */
export function pickOperatorRow(rpcJson, userId) {
  if (!rpcJson || !userId) return null;
  const ops = Array.isArray(rpcJson.operators) ? rpcJson.operators : [];
  return ops.find(o => o && o.operator_id === userId) || null;
}

/** RPC-Zeile → die vier Kachel-Zähler. `prep` fasst Speisen + Getränke zusammen
 *  (die Kachel hat vier Spalten, der Küchen-/Theken-Split steht in der
 *  Vollansicht Personal). Null-tolerant. Pure. */
export function shiftCounters(row) {
  const n = (v) => Number(v) || 0;
  if (!row) return { taken: 0, prep: 0, served: 0, paid: 0 };
  return {
    taken:  n(row.taken_orders),
    prep:   n(row.prep_food) + n(row.prep_drink),
    served: n(row.served),
    paid:   n(row.paid_bons)
  };
}

/** Sub-Zeile der Kachel: „Heute · seit HH:MM", sobald der Schicht-Beginn der
 *  Operator-Session bekannt ist (operator-session.js liefert `startedAt` aus
 *  `operator_sessions.started_at`), sonst nur „Heute". Pure. */
export function shiftSubLabel(op) {
  const iso = op && op.startedAt;
  if (!iso) return 'Heute';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Heute';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `Heute · seit ${hh}:${mm} Uhr`;
}

/* -------------------------------------------------------------------------- */
/* Renderer                                                                    */
/* -------------------------------------------------------------------------- */

/** Uhr-Icon im Stil der übrigen Dashboard-Kacheln (bewusst KEIN Euro-Icon). */
const SHIFT_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>';

const SHIFT_COLUMNS = Object.freeze([
  { key: 'taken',  label: 'Aufgenommen', title: 'Von dir aufgenommene Bestellungen' },
  { key: 'prep',   label: 'Zubereitet',  title: 'Von dir fertig gemeldete Speisen + Getränke' },
  { key: 'served', label: 'Serviert',    title: 'Von dir ausgegebene / servierte Bestellungen' },
  { key: 'paid',   label: 'Kassiert',    title: 'Von dir kassierte Bons' }
]);

/**
 * Die Kachel. Ohne Operator: Anmelde-Aufforderung mit demselben Einstieg wie
 * die Operator-Kachel (`operatorOpenSwitch()`). Mit Operator: reine Anzeige —
 * kein Klick-Ziel, damit der Staff-Lock (dashboard-locked) keinen Sonderfall
 * braucht und niemand aus der Sicht geworfen wird.
 */
export function renderShiftWidget({ op, counters, loading } = {}) {
  if (!op) {
    return `
      <div class="widget is-clickable widget-accent" id="dash-shift-card"
           onclick="operatorOpenSwitch()" tabindex="0" title="Operator anmelden">
        <div class="widget-head">
          <span class="widget-label">Meine Schicht</span>
          <span class="widget-icon" aria-hidden="true">${SHIFT_ICON}</span>
        </div>
        <div class="widget-value">—</div>
        <div class="widget-sub">Operator anmelden, um deine Tätigkeiten zu sehen</div>
      </div>
    `;
  }
  const c = counters || { taken: 0, prep: 0, served: 0, paid: 0 };
  const cols = SHIFT_COLUMNS.map(col => `
        <div class="widget-quad-col" title="${htmlEscape(col.title)}">
          <div class="widget-quad-value${loading ? '' : ' countup'}">${loading ? '·' : (Number(c[col.key]) || 0)}</div>
          <div class="widget-quad-label">${col.label}</div>
        </div>`).join('');
  return `
    <div class="widget widget-quad" id="dash-shift-card">
      <div class="widget-head">
        <span class="widget-label">Meine Schicht</span>
        <span class="widget-icon" aria-hidden="true">${SHIFT_ICON}</span>
      </div>
      <div class="widget-quad-grid">${cols}</div>
      <div class="widget-sub" style="margin-top: var(--space-3);">${htmlEscape(shiftSubLabel(op))}</div>
    </div>
  `;
}

/* -------------------------------------------------------------------------- */
/* Loader (RPC) + non-destruktiver Kachel-Patch                                */
/* -------------------------------------------------------------------------- */

function _operator() {
  const api = (typeof window !== 'undefined') && window.App && window.App.operator;
  return (api && typeof api.getOperator === 'function') ? api.getOperator() : null;
}

function _supa() {
  return (typeof window !== 'undefined' && window.App && window.App.supabase) || null;
}

// Modul-Cache: { key: 'usr_x|2026-08-25', counters } — überlebt Re-Render,
// damit die Kachel beim Realtime-Rerender nicht auf 0 zurückspringt.
let _cache = null;
let _inflight = false;
let _debounce = null;

/** Cache verwerfen (Operator-Wechsel). */
export function clearShiftCache() {
  _cache = null;
}

/** Nur für Vitest: kompletten Modul-State zurücksetzen. */
export function _resetShiftStateForTests() {
  _cache = null;
  _inflight = false;
  if (_debounce) { clearTimeout(_debounce); _debounce = null; }
}

/** Aus dem Cache gelesene Zähler für den aktuellen Operator (oder null). */
export function cachedShiftCounters(op) {
  if (!_cache || !op) return null;
  return _cache.key === `${op.userId}|${todayISO()}` ? _cache.counters : null;
}

/** Kachel-DOM an Ort und Stelle ersetzen — KEIN renderDashboard (Sturm-Schutz). */
function _patchShiftCard() {
  if (typeof document === 'undefined') return;
  const el = document.getElementById('dash-shift-card');
  if (!el) return;   // Dashboard nicht aktiv — der nächste Render zieht den Cache
  const op = _operator();
  const counters = cachedShiftCounters(op);
  el.outerHTML = renderShiftWidget({ op, counters, loading: counters === null });
}

async function _loadNow() {
  const op = _operator();
  if (!op || !op.userId) return;
  const supa = _supa();
  if (!supa || typeof supa.rpc !== 'function') return;
  if (_inflight) return;
  _inflight = true;
  const today = todayISO();
  try {
    const { data, error } = await supa.rpc('stats_operator_productivity', {
      p_from: today, p_to: today
    });
    if (error) throw error;
    // Operator kann während des Requests gewechselt haben → Antwort verwerfen.
    const now = _operator();
    if (!now || now.userId !== op.userId) return;
    _cache = {
      key: `${op.userId}|${today}`,
      counters: shiftCounters(pickOperatorRow(data, op.userId))
    };
    _patchShiftCard();
  } catch (e) {
    // Nicht-fatal: die Kachel bleibt auf dem letzten Stand bzw. auf 0.
    console.warn('[dashboard-shift] stats_operator_productivity:', e && (e.message || e));
  } finally {
    _inflight = false;
  }
}

/** Debounced Refresh (1,5 s trailing) — bündelt Realtime-/Event-Bursts. */
export function refreshShiftCounters({ immediate = false } = {}) {
  if (immediate) { _loadNow(); return; }
  if (_debounce) clearTimeout(_debounce);
  _debounce = setTimeout(() => { _debounce = null; _loadNow(); }, 1500);
}

/* -------------------------------------------------------------------------- */
/* Event-Verdrahtung                                                           */
/* -------------------------------------------------------------------------- */

const SHIFT_EVENTS = Object.freeze([
  'order:created',
  'order:status_changed',
  'order:paid',
  'order:realtime',
  'order:round_status:realtime'
]);

let _wired = false;

/** Einmalige Event-Verdrahtung. Die Order-Events refreshen debounced, ein
 *  Operator-Wechsel verwirft zusätzlich den Cache (sonst zeigte die Kachel
 *  kurz die Zahlen des vorigen Operators). */
export function wireShiftEvents() {
  if (_wired) return;
  const App = (typeof window !== 'undefined') && window.App;
  if (!App || !App.events || typeof App.events.on !== 'function') return;
  _wired = true;
  for (const ev of SHIFT_EVENTS) App.events.on(ev, () => refreshShiftCounters());
  App.events.on('operator:changed', () => {
    clearShiftCache();
    refreshShiftCounters();
  });
}
